import type { CSSProperties, FunctionalComponent, Ref } from 'vue'
import type { TFormModel } from './formModel'
import type { IInputProps, RangeOptions } from '../input/types'
import type { TStyleVariant } from '@/types/style/variant'
import { isFunction } from '@/utils/is'
import type { IconFactor } from '@/types/elements/icon'
import type { FormValidator } from '../validation'
import type { IFormErrorMessageProps } from '../form-error-message/types'
import type { TFormFieldDict } from './field'
import type { FindexFile } from '@/types/file'
import type { FormFieldOptions } from './fieldOptions'
import type { StringIndexable } from '@/types/object'
import type { IBubbletipProps } from 'components/bubbletip/types'
import type { GridProps } from 'components/grid/types'
import type { IBaseComponentProps } from 'components/base/types'
import type { IAttachment, ISavedDocument, IValue, TDocRef } from 'findef'
import type { Vector } from 'math'
import type { DBObject } from '@/types/dbObject'

export type BaseFormFactor =
  | string
  | number
  | boolean
  | IValue
  | number[]
  | Vector
  | Date
  | undefined
  | null
  | IAttachment
  | IAttachment[]
  | ISavedDocument<IAttachment>
  | ISavedDocument<IAttachment>[]
  | TDocRef<IAttachment>
  | string[]

export type TFormFunction<
  T extends TFormModel = TFormModel,
  ContextType extends StringIndexable | undefined = StringIndexable,
  ReturnType = any
> = (args: { state: T; path: string; fields?: TFormFieldDict<T>; context?: Ref<ContextType> }) => ReturnType

export type MaybeFormFunction<Ret, T extends TFormModel = TFormModel> =
  Ret | ((state: T) => Ret);

export type TFormFactor<
  T extends TFormModel = TFormModel,
  ContextType extends StringIndexable | undefined = StringIndexable
> =
  | BaseFormFactor
  | FindexFile[]
  | FindexFile
  | File
  | File[]
  | Ref<TFormFactor>
  | TFormFunction<T, ContextType>
  | DBObject

type DisplayOptions = {
    value?: string
  title?: string
  body?: FunctionalComponent<any>;
  icon?: IconFactor;
};

export type IFormFieldAttributes<
  T extends TFormModel = TFormModel,
  K extends TFormModel = T,
  ContextType extends StringIndexable | undefined = StringIndexable
> = {
  $value?: TFormFactor<K, ContextType>
  $name?: string
  $computedValue?: (state: K) => TFormFactor
  $customComponent?: FunctionalComponent<IInputProps & { state: K }>
  $wrapper?: FunctionalComponent<IInputProps>
  $wrapperClass?: string | string[]
  $wrapperCSS?: string | CSSProperties
  $css?: string | CSSProperties
  $layout?: 'horizontal' | 'vertical' | 'custom'
  $grid?: Omit<GridProps, keyof IBaseComponentProps>
  $layoutClass?: string
  $formId?: string
  $schemaId?: string
  $label?: string | FunctionalComponent<any>
  $labelClass?: string
  $labelPlacement?: 'left' | 'right' | 'center' | 'default'
  $subLabel?: string
  $after?: FunctionalComponent<IInputProps & { state: K }>
  $before?: FunctionalComponent<IInputProps & { state: K }>
  $inlineLabel?: boolean
  $tooltip?: string
  $bubbleTip?: Omit<IBubbletipProps, 'triggerRef'>
  $title?: string
  $icon?: IconFactor
  $placeholder?: string | TFormFunction<K, ContextType> | Ref<string>
  $placeholderIcon?: IconFactor
  $placeholderClass?: string | string[]
  $subtitle?: string
  $subtitleClass?: string
  $validators?: FormValidator<T>[]
  $errorProps?: Partial<IFormErrorMessageProps>
  $type?: string
  $numberPrecision?: string
  $componentType?: string
  $injectedProps?: StringIndexable | TFormFunction<T, ContextType>
  $variant?: TStyleVariant
  $locked?: boolean | TFormFunction<K, ContextType>
  $lockedReason?: string | TFormFunction<K, ContextType>
  $hidden?: boolean | TFormFunction<K, ContextType>
  $numberTweening?: boolean
  $touched?: boolean
  $dependants?: string[]
  $clearFieldsIfNotTrue?: string[]
  $focused?: boolean
  $changeReportedCallback?: (field: IInputProps) => void
  $options?: FormFieldOptions<T>
  $context?: Ref<ContextType>
  $display?: DisplayOptions | TFormFunction<K, ContextType, DisplayOptions>;
  $placement?: 'top' | 'bottom' | 'left' | 'right'
  $range?: RangeOptions
}

export const isFormFunction = <
  T extends TFormModel = TFormModel,
  ContextType extends StringIndexable | undefined = StringIndexable
>(
  x: any,
  key: string
): x is TFormFunction<T, ContextType> => {
  return isFunction(x) && ['$hidden', '$value', '$injectedProps', '$display'].includes(key)
}
